import Vue, { Component, defineAsyncComponent } from "vue";
import VueRouter, {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocation,
  RouteLocationNormalized,
  RouteLocationNormalizedGeneric,
  RouteLocationRaw,
  Router,
  RouteRecordRaw,
} from "vue-router";
import d from "debug";
import OrganisationPage from "../views/admin/organisation-page.vue";
import AccreditedUserAreaOrganisationListPage from "../views/admin/accredited-user-area-organisation-list-page.vue";
import AccreditedUserAreaOrganisationDetailPage from "../views/admin/accredited-user-area-organisation-detail-page.vue";
import UserProfile from "../views/user-profile.vue";
import TeamOutcomes from "../views/team/team-outcomes.vue";
import TeamSettings from "../views/team/team-settings.vue";
import cms from "../services/cms/cms";
import { csContext } from "../services/cs-context";
import { setTitle } from "../services/utilities/set-title";
import DevelopSummaryPage from "../views/develop-summary-page.vue";
import SubscriptionPolicy from "../components/subscription-policy.vue";
import {
  CmsDocument,
  CmsDocumentTypes,
  CmsRichText,
  webAppUrlMap,
} from "../services/cms/cms-type-definitions";

import { trackingHooks } from "../services/cms/cms-tracking-hooks";
import StrengthPage from "../views/cms/strength-page.vue";
import NotPremiumMember from "../views/error-pages/not-premium-member.vue";
import Registration from "../views/registration-page.vue";
import Login from "../views/login-page.vue";
import SwipeNavigationInfo from "../components/swipe-navigation-info";
import CreateTeam from "../views/team/create-team.vue";
import TeamInvite from "../views/team/team-invite.vue";
import ReportReadyPage from "../views/report-ready-page.vue";
import TeamMembers from "../views/team/team-members.vue";
import DeleteUserPage from "../views/admin/delete-user-page.vue";
import OrganisationsPage from "../views/admin/organisations-page.vue";
import HeatmapViewer from "../views/heatmap-viewer.vue";
import MyReports from "../views/my-journey/my-reports.vue";
import OrganisationReport from "../views/admin/organisation-report.vue";
import TeamEffectivenessHighlightsPage from "../views/team-effectiveness/team-effectiveness-highlights-page.vue";
import MyAccount from "../views/my-account/my-account.vue";
import InvitesRestricted from "../views/error-pages/invites-restricted.vue";
import EventLog from "../views/admin/event-log/event-log.vue";
import { specificCmsPages } from "./specific-cms-pages";
import CmsPage from "../views/cms/cms-page.vue";
import TeamPathway from "../components/team-outcomes/team-pathway.vue";
import SelectTeamOutcome from "../components/team-outcomes/select-team-outcome.vue";
import ViewPathway from "../components/team-outcomes/view-pathway.vue";
import SelectTeamPathway from "../components/team-outcomes/select-team-pathway.vue";
import NotFound from "../views/error-pages/not-found.vue";
import CompletePathway from "../views/team/complete-pathway.vue";
import GroupDetails from "../views/admin/groups/group-details-page.vue";
import AccreditedUserAreaGroupDetails from "../views/admin/groups/accredited-user-area-group-details-page.vue";
import PersonalOutcomes from "../views/personal-outcomes/personal-outcomes.vue";
import SelectPersonalOutcome from "../components/personal-outcomes/select-personal-outcome.vue";
import PersonalPathways from "../components/personal-outcomes/personal-pathways.vue";
import SelectPersonalPathway from "../components/personal-outcomes/select-personal-pathway.vue";
import PersonalPathwaySummary from "../components/personal-outcomes/personal-pathway-summary.vue";
import CompletePersonalPathway from "../views/personal-outcomes/complete-personal-pathway.vue";
import ViewpointsPathwayContainer from "../views/viewpoints-pathways/viewpoints-pathway-container.vue";
import ViewpointsReportPathwayContainer from "../views/viewpoints-pathways/viewpoints-report-pathway-container.vue";
import GuestHomePage from "../views/guest/guest-home-page.vue";
import TrialLimitExceeded from "../views/error-pages/trial-limit-exceeded.vue";
import JyreRecommends from "../views/jyre-recommends.vue";
import AdHocGroupReport from "../views/ad-hoc-report/ad-hoc-group-report.vue";
import DemoPage from "../views/demo/demo-page.vue";
import ScratchpadPage from "../views/scratchpad-page.vue";
import ViewpointsRequired from "../views/error-pages/viewpoints-required.vue";
import InviteAcceptPage from "../views/invite-accept-page.vue";

// eslint-disable-next-line no-unused-vars
type ErrorHandler = (Error) => void;

SwipeNavigationInfo.init();

const debug = d("cs.Router");

export const slidingLayerCount = 9;

export interface CustomRouter extends Router {
  isCurrentRouteInSlidingStack: boolean;
  getSlidingRoute(
    href: string,
    from: RouteLocationNormalized,
    replace: boolean,
  ): RouteLocationNormalized;
  openInNewTab(to: RouteLocationNormalized): void;
  handleRouteChange(href: string, e: MouseEvent | undefined, replace: boolean): void;
  handleClick(href: string | null, e: MouseEvent | undefined, replace?: boolean): void;
  closeSlidingStack(): void;
}

const notFoundRouteName = "NotFound";

const router = createRouter({
  history: createWebHistory(),
  routes: [],
  scrollBehavior: (to, from, savedPosition) => {
    if (from.fullPath === "/" || (to && to.meta && to.meta.handlesOwnScrolling)) {
      return undefined; // Let the route/component handle its own scrolling (e.g. sessions)
    }

    if (savedPosition) {
      return savedPosition;
    }

    return { left: 0, top: 0 };
  },
}) as CustomRouter;

Object.defineProperties(router, {
  isCurrentRouteInSlidingStack: {
    get() {
      return !!this.currentRoute.query?.sliding1;
    },
    enumerable: true,
  },
});

router.openInNewTab = function openInNewTab(to: RouteLocationNormalized) {
  const route = this.resolve(to);
  window.open(route.href, "_blank");
};

router.getSlidingRoute = function getSlidingRoute(
  href: string,
  from: RouteLocationNormalized,
  replace: boolean,
): RouteLocationNormalized {
  const clone = { query: { ...from.query } };
  for (let i = 1; i <= slidingLayerCount; i++) {
    const slidingX = `sliding${i}`;

    if (!from.query[slidingX] || i === slidingLayerCount) {
      // Make it so you can't accidentally double-stack navigations.
      if (from.query[`sliding${i - 1}`] !== href) {
        if (replace) {
          clone.query[`sliding${i - 1}`] = href;
        } else {
          clone.query[slidingX] = href;
        }
      }

      break;
    }
  }
  return clone as RouteLocationNormalized;
};

router.handleRouteChange = function handleRouteChange(
  href: string,
  e: MouseEvent | undefined,
  replace: boolean,
): void {
  let to: RouteLocation | RouteLocationNormalized = this.resolve(href);
  debug("handle route change", { to });
  const vueRouterShouldBeUsedForThisNavigation =
    !!to &&
    to.matched &&
    to.matched.length > 0 &&
    !to.meta?.pageIsServerRendered &&
    to.name !== notFoundRouteName;

  if (vueRouterShouldBeUsedForThisNavigation) {
    e?.preventDefault();
    const from = this.currentRoute.value;
    if (to.meta?.willOpenInNewTab) {
      this.openInNewTab(to as RouteLocationNormalizedGeneric);
    } else {
      if (from.meta?.canBeSlidOver && to.meta?.canSlide) {
        to = this.getSlidingRoute(href, from, replace);
      }

      // Make route change
      if (replace) {
        this.replace(to);
      } else {
        this.push(to);
      }
    }
  }
};

router.handleClick = function handleClick(
  href: string | null,
  e: MouseEvent | undefined,
  replace = false,
): void {
  debug("handleClick", { href, e, replace });
  if (href && href.startsWith("/")) {
    this.handleRouteChange(href, e, replace);
  }
};

router.closeSlidingStack = function closeSlidingStack(): void {
  if (!this.isCurrentRouteInSlidingStack) {
    return;
  }

  const clone = { query: {} };
  clone.query = { ...this.currentRoute.value.query };

  for (let i = slidingLayerCount; i >= 1; i--) {
    const slidingX = `sliding${i}`;
    if (this.currentRoute.value.query[slidingX]) {
      clone.query[slidingX] = "";
    }
  }

  this.push(clone);
};

// TODO: move this to happen in a beforeEach hook, we can't inherit the original push from the router
// so this is probably a better option.

// router.push = function push(
//   location: RouteLocationRaw,
//   // eslint-disable-next-line @typescript-eslint/ban-types
//   onComplete?: Function | undefined,
//   onAbort?: ErrorHandler | undefined,
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
// ): any {
//   let newLoc = location;

//   if (csContext.currentTeam?.id) {
//     if (typeof newLoc === "string") {
//       newLoc = { path: newLoc };
//     }

//     newLoc.query ??= {};

//     if (newLoc.query.team === undefined) {
//       newLoc.query.team = csContext.currentTeam.id;
//     }
//   }

//   return super.push(newLoc, onComplete, onAbort);
// }

// TODO: register router at the point where the app is created.
// Vue.use(VueRouter);

// ---- Begin Route Config ----

const serverRenderedCmsTypes: CmsDocumentTypes[] = [
  CmsDocumentTypes.CmsSession,
  CmsDocumentTypes.CmsLeaderType,
  CmsDocumentTypes.CmsStrengthItem,
];

function propsFunction(r: RouteLocationNormalized) {
  return { uid: r.params.uid, type: r.meta?.type };
}

export function slidingHref(r: RouteLocationNormalized, slidingX: string): string {
  if (slidingX === "default") {
    return r.path;
  }

  const queryParam = r.query[slidingX];
  let href = "";

  if (typeof queryParam === "object") {
    href = queryParam?.[0] ?? "";
  } else if (typeof queryParam === "string") {
    href = queryParam;
  } else {
    href = "";
  }
  return href;
}

function getSlidingPropFunction(slidingX: string) {
  return (r: RouteLocationNormalized) => {
    const href = slidingHref(r, slidingX);
    if (href) {
      const resolved = router.resolve(href);
      // eslint-disable-next-line
      // @ts-ignore
      debug("slidingpropsfunction", { href, resolved });
      const matchedProps =
        (resolved.matched[0]?.props as any)?.default ?? resolved.matched[0].props;

      if (typeof matchedProps === "function") {
        return matchedProps(resolved);
      }
      if (typeof matchedProps === "object") {
        return matchedProps;
      }

      return {};
    }
    return {};
  };
}

function slidingRoute(type: string) {
  const path = // This is a workaround for the fact that I forgot some paths have guids. Will have to implement a different way in the future, other than recognising
    `${webAppUrlMap[type]}/:xxx([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?/:uid?/:id([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?`.replace(
      "//",
      "/",
    );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const components: RouteRecordRaw["components"] = { default: CmsPage };
  const props: RouteRecordRaw["props"] = { default: propsFunction };
  const name = type;

  const meta = {
    type,
    pageIsServerRendered: serverRenderedCmsTypes.includes(type as CmsDocumentTypes),
    canBeSlidOver:
      type === CmsDocumentTypes.CmsResource ||
      type === CmsDocumentTypes.CmsSession ||
      type === CmsDocumentTypes.CmsDevelopPage ||
      type === CmsDocumentTypes.CmsSessionWithSlides ||
      type === CmsDocumentTypes.CmsReport ||
      type === CmsDocumentTypes.CmsTeamPathwayStep ||
      type === CmsDocumentTypes.CmsPersonalPathwayStep ||
      type === CmsDocumentTypes.CmsGoalCapturePage,
    willOpenInNewTab:
      type !== CmsDocumentTypes.CmsResource &&
      type !== CmsDocumentTypes.CmsDevelopPage &&
      type !== CmsDocumentTypes.CmsSmartJournalPage &&
      type !== CmsDocumentTypes.CmsReportPage &&
      type !== CmsDocumentTypes.CmsGoalCapturePage &&
      type !== CmsDocumentTypes.CmsPersonalPathway &&
      type !== CmsDocumentTypes.CmsPersonalPathwayStep &&
      type !== CmsDocumentTypes.CmsTeamPathway &&
      type !== CmsDocumentTypes.CmsTeamPathwayStep &&
      type !== CmsDocumentTypes.CmsSessionWithSlides,
    canSlide:
      type === CmsDocumentTypes.CmsResource ||
      type === CmsDocumentTypes.CmsDevelopPage ||
      type === CmsDocumentTypes.CmsReport ||
      // type === CmsDocumentTypes.CmsSessionWithSlides ||
      type === CmsDocumentTypes.CmsSmartJournalPage ||
      type === CmsDocumentTypes.CmsReportPage ||
      type === CmsDocumentTypes.CmsGoalCapturePage,
    hideFooter:
      type === CmsDocumentTypes.CmsSession ||
      type === CmsDocumentTypes.CmsSessionWithSlides ||
      type === CmsDocumentTypes.CmsTeamPathwayStep ||
      type === CmsDocumentTypes.CmsPersonalPathwayStep ||
      type === CmsDocumentTypes.CmsReportPage ||
      type === CmsDocumentTypes.CmsGoalCapturePage,
    hideNavigation:
      type === CmsDocumentTypes.CmsSession ||
      type === CmsDocumentTypes.CmsSessionWithSlides ||
      type === CmsDocumentTypes.CmsCustomStrengthMapping ||
      type === CmsDocumentTypes.CmsPersonalPathwayStep ||
      type === CmsDocumentTypes.CmsTeamPathwayStep,
    handlesOwnScrolling:
      type === CmsDocumentTypes.CmsSession ||
      type === CmsDocumentTypes.CmsSessionWithSlides ||
      type === CmsDocumentTypes.CmsResource ||
      type === CmsDocumentTypes.CmsDevelopPage ||
      type === CmsDocumentTypes.CmsTeamPathwayStep ||
      type === CmsDocumentTypes.CmsPersonalPathwayStep,
    trackingHook: trackingHooks[type] || undefined,
  };

  for (let i = 1; i <= slidingLayerCount; i++) {
    const slidingX = `sliding${i}`;

    if (components) {
      components[slidingX] = CmsPage as any;
    }

    props[slidingX] = getSlidingPropFunction(slidingX);
  }

  const routeConfig: RouteRecordRaw = {
    path,
    components,
    props,
    name,
    meta,
    children: [], // Add an empty array for the children property
  };

  return routeConfig;
}

const generalCmsRoutes = () =>
  Object.keys(webAppUrlMap)
    .filter((type) => webAppUrlMap[type])
    .map((r) => slidingRoute(r));

const specificCmsRoutes = (): RouteRecordRaw[] => {
  const routes = specificCmsPages.map((p) => ({
    root: p.root,
    path: `${p.root}/:id?`.replace("//", "/"),
    component: CmsPage,
    meta: {
      type: p.type,
      uid: p.uid,
      canSlide: p.canSlide,
      canBeSlidOver: p.canBeSlidOver,
      hideNavigation: p.hideNavigation,
      hideFooter: p.hideFooter,
      trackingHook: trackingHooks[p.type] || undefined,
    },
    props: {
      type: p.type,
      uid: p.uid,
    },
  }));

  return routes.map((r) => {
    if (r.meta.canBeSlidOver) {
      const route = slidingRoute(r.meta.type);
      route.meta!.uid = r.meta.uid;
      return route;
    }

    return r;
  }) as RouteRecordRaw[];
};

// 2. Define some routes
// Each route should map to a component. The "component" can
// either be an actual component constructor created via
// `Vue.extend()`, or just a component options object.
// We'll talk about nested routes later.
const routes: RouteRecordRaw[] = [
  {
    // Until Vue knows about 100% of the routes on the server,
    // the NotFound page must always be a full page reload.
    // The name of this route is used to force this effect.
    name: notFoundRouteName,
    path: "/:pathMatch(.*)*",
    component: NotFound as any,
  },
  {
    path: "/team/create",
    component: CreateTeam as any,
    meta: { title: "Create your team" },
  },
  {
    path: "/ad-hoc-group-report/:name/:userIds",
    component: AdHocGroupReport as any,
    props: true,
    meta: { title: "Ad Hoc Report" },
    name: "adHocGroupReport",
  },
  {
    path: "/team/settings/:id",
    component: TeamSettings as any,
    props: true,
    meta: { title: "Team settings" },
  },
  {
    path: "/team/members/:id",
    component: TeamMembers as any,
    props: true,
    meta: { title: "Team members" },
  },
  {
    path: "/team/invite/:id",
    component: TeamInvite as any,
    props: true,
    meta: { title: "Invite people to your team" },
  },
  {
    path: "/user/delete/:id",
    component: DeleteUserPage as any,
    props: true,
    meta: { title: "Delete user" },
  },
  {
    path: "/team/heatmap/:id",
    component: HeatmapViewer as any,
    props: true,
    meta: { title: "Heatmap", heatmapType: "team" },
  },
  {
    path: "/organisation/heatmap/:id",
    component: HeatmapViewer as any,
    props: true,
    meta: { title: "Heatmap", heatmapType: "organisation" },
  },
  {
    path: "/organisation/",
    component: OrganisationsPage as any,
    props: true,
    meta: { title: "Manage Organisations" },
  },
  {
    path: "/organisation/details/:id",
    component: OrganisationPage as any,
    props: true,
    meta: { title: "Organisation main page" },
  },
  {
    path: "/organisation/report/:id",
    component: OrganisationReport as any,
    props: true,
    meta: { title: "Organisation report page" },
  },
  // My Organisation routes - externally facing organisation management portal
  // Note: This is different from the /organisation/ routes which are for internal organisation data/reports
  {
    path: "/myorganisation/",
    component: AccreditedUserAreaOrganisationListPage as any,
    props: true,
    meta: { title: "Manage My Organisation" },
  },
  {
    path: "/myorganisation/details/:id",
    component: AccreditedUserAreaOrganisationDetailPage as any,
    props: true,
    meta: { title: "My Organisation Portal" },
  },
  {
    path: "/myorganisation/report/:id",
    component: OrganisationReport as any,
    props: true,
    meta: { title: "My Organisation Report" },
  },
  {
    path: "/myorganisation/heatmap/:id",
    component: HeatmapViewer as any,
    props: true,
    meta: { title: "Heatmap", heatmapType: "organisation" },
  },
  {
    path: "/myorganisation/group/details/:teamId",
    component: AccreditedUserAreaGroupDetails as any,
    props: true,
    meta: {
      title: "My Organisation Group Details",
      canBeSlidOver: false,
    },
  },
  {
    path: "/team/effectiveness/:id?",
    component: TeamEffectivenessHighlightsPage as any,
    props: true,
    meta: { title: "Team Effectiveness Review" },
  },
  {
    path: "/review/reportready/:id",
    component: ReportReadyPage as any,
    props: true,
    meta: { title: "Results are ready" },
  },
  {
    path: "/team/outcomes/:id?",
    component: TeamOutcomes as any,
    props: true,
    name: "teamOutcomes",
    beforeEnter: (to, from, next) => {
      if (to.name === "teamOutcomes") {
        next(`/team/outcomes/${to.query.team}/select`);
      }
      next();
    },
    meta: { title: "Team Outcomes" },
    children: [
      {
        path: "select",
        name: "selectTeamOutcome",
        component: SelectTeamOutcome as any,
      },
      {
        path: "outcome/:outcomeUid",
        name: "selectPathway",
        component: TeamPathway as any,
        children: [
          {
            path: "select",
            name: "selectTeamPathway",
            component: SelectTeamPathway as any,
          },
          {
            path: "pathway/:pathwayUid",
            name: "viewPathway",
            component: ViewPathway as any,
          },
        ],
      },
    ],
  },
  {
    path: "/my/outcomes",
    component: PersonalOutcomes as any,
    props: true,
    name: "personalOutcomesRouterView",
    beforeEnter: (to, from, next) => {
      if (to.name === "personalOutcomesRouterView") {
        next("/my/outcomes/select");
      }
      next();
    },
    meta: { title: "Personal Outcomes Router View" },
    children: [
      {
        path: "select",
        name: "selectPersonalOutcome",
        component: SelectPersonalOutcome as any,
      },
      {
        path: "outcome/:outcomeUid",
        name: "personalPathways",
        component: PersonalPathways as any,
        children: [
          {
            path: "select",
            name: "selectPersonalPathway",
            component: SelectPersonalPathway as any,
          },
          {
            path: "pathway/:pathwayUid",
            name: "personalPathwaySummary",
            component: PersonalPathwaySummary as any,
          },
        ],
      },
    ],
  },
  {
    path: "/team/complete/",
    name: "completePathway",
    component: CompletePathway as any,
  },
  {
    path: "/my/complete/",
    name: "completePersonalPathway",
    component: CompletePersonalPathway as any,
  },
  {
    path: "/develop/",
    component: DevelopSummaryPage as any,
    props: true,
    meta: { title: "Develop", canBeSlidOver: false },
  },
  {
    path: "/my/reports",
    component: MyReports as any,
    props: true,
    meta: { title: "My Reports", canBeSlidOver: false },
  },
  {
    path: "/register/withinvite/:code",
    component: Registration as any,
    props: true,
    meta: {
      title: "Registration",
      canBeSlidOver: false,
      hideNavigation: true,
      hideFooter: true,
    },
  },
  {
    path: "/myaccount",
    component: MyAccount as any,
    props: false,
    meta: {
      title: "My Account",
      canBeSlidOver: false,
    },
  },
  {
    path: "/restrictedinvites/:reason",
    component: InvitesRestricted as any,
    props: false,
    meta: {
      title: "Invites Restricted",
      canBeSlidOver: false,
      hideNavigation: true,
      hideFooter: true,
    },
  },
  {
    path: "/invite/accept/:code",
    component: InviteAcceptPage as any,
    props: true,
    meta: {
      title: "Accept Invite",
    },
  },
  {
    path: "/login",
    component: Login as any,
    props: false,
    meta: {
      title: "Login Page",
      canBeSlidOver: false,
      hideNavigation: true,
      hideFooter: true,
    },
  },
  {
    path: "/subscriptionpolicy",
    component: SubscriptionPolicy as any,
    props: false,
    meta: {
      title: "Subscription Policy",
      hideNavigation: true,
      canBeSlidOver: false,
    },
  },
  {
    path: "/admin/eventlog",
    component: EventLog as any,
    props: false,
    meta: {
      title: "Event Log",
      canBeSlidOver: false,
    },
  },
  {
    path: "/admin/group/details/:teamId",
    component: GroupDetails as any,
    props: false,
    meta: {
      title: "Group Details",
      canBeSlidOver: false,
    },
  },
  {
    path: "/NotPremiumMember",
    component: NotPremiumMember as any,
    props: false,
    meta: {
      title: "Error - Not Premium Member",
      canBeSlidOver: false,
    },
  },
  {
    path: "/account/forgotpassword",
    props: false,
    component: undefined as any,
    meta: {
      pageIsServerRendered: true,
    },
  },
  {
    path: "/user/profile/:id",
    component: UserProfile as any,
    props: true,
    meta: {
      title: "Profile",
      canBeSlidOver: false,
    },
  },
  {
    path: "/my/pathway/viewpoints-invite",
    component: ViewpointsPathwayContainer as any,
    props: true,
    meta: {
      title: "Viewpoints Pathway",
      hideNavigation: true,
      pageIsServerRendered: true,
    },
  },
  {
    path: "/my/pathway/working-with-your-viewpoints-report",
    component: ViewpointsReportPathwayContainer as any,
    props: true,
    meta: {
      title: "Viewpoints Report Pathway",
      hideNavigation: true,
      pageIsServerRendered: true,
    },
  },
  {
    path: "/home/guest",
    component: GuestHomePage as any,
    props: true,
    meta: {
      title: "Guest Home Page",
      canBeSlidOver: false,
    },
  },
  {
    path: "/triallimitexceeded",
    component: TrialLimitExceeded as any,
    props: false,
    meta: {
      name: "Error - Trial Limit Exceeded",
      canBeSlidOver: false,
    },
  },
  {
    path: "/jyrerecommends",
    component: JyreRecommends as any,
    props: true,
    meta: {
      title: "Jyre Recommends",
    },
  },
  {
    path: "/demo",
    component: DemoPage as any,
    props: false,
    meta: {
      title: "Demo Page",
    },
  },
  {
    path: "/scratchpad",
    component: ScratchpadPage as any,
    props: true,
    meta: {
      title: "Smart Scratchpad",
    },
  },
  {
    path: "/errorpages/viewpointsrequired",
    component: ViewpointsRequired as any,
    props: false,
    meta: {
      title: "Error - Viewpoints Required",
      canBeSlidOver: false,
    },
  },
  {
    path: "/resources/strengths/:xxx([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?/:strength?/:id([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?",
    component: StrengthPage as any,
    props: false,
    meta: {
      title: "Strength",
      canBeSlidOver: false,
    },
  },

  ...specificCmsRoutes(),
  ...generalCmsRoutes(),
];

// ---- End Route Config ----;

routes.forEach((r) => {
  router.addRoute(r);
});

let isFirstRouterCall = true;

// Use the router to add the team to the url if we're sent here via a link with no team context in the url.
// router.beforeEach((to, from, next) => {
//   if (!to.fullPath.includes("team=") && csContext.currentTeam?.id && isFirstRouterCall) {
//     next({
//       path: to.fullPath,
//       query: { team: csContext.currentTeam?.id },
//       replace: true,
//     });
//   } else {
//     next();
//   }
//   isFirstRouterCall = false;
// });

router.beforeEach(
  (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    // Check if we need to add the team to the URL
    if (!to.fullPath.includes("team=") && csContext?.currentTeam?.id && isFirstRouterCall) {
      const query = { ...to.query };

      isFirstRouterCall = false;
      next({
        path: to.fullPath,
        query: { ...query, team: csContext.currentTeam?.id },
        replace: true,
      });
      return;
    }

    isFirstRouterCall = false;

    // Modify the query with the team parameter if missing
    if (csContext.currentTeam?.id) {
      const newLoc = { ...to, query: { ...to.query } };

      if (newLoc.query.team === undefined) {
        newLoc.query.team = csContext.currentTeam.id;
        next(newLoc);
        return;
      }
    }

    next();
  },
);

router.beforeResolve((to, from, next) => {
  debug(`Routing to ${to.fullPath} from ${from.fullPath}`, { to, from });
  next();
});

function getTypeFromRoute(to: RouteLocationNormalized) {
  return to.meta?.type ?? (to.matched[0]?.props as any)?.default?.type;
}

function getUidFromRoute(to: RouteLocationNormalized) {
  return (to.meta?.uid ?? to.params.uid ?? (to.matched[0]?.props as any)?.default?.uid) as string;
}

async function getDoc(to: RouteLocationNormalized) {
  let type = getTypeFromRoute(to);
  let uid = getUidFromRoute(to);

  if (!!type && !!uid) {
    for (let i = 1; i <= slidingLayerCount; i++) {
      const slidingX = `sliding${i}`;
      if (to.query[slidingX]) {
        const slidingToPath = to.query[slidingX] as string;
        const slidingToRoute = router.resolve({ path: slidingToPath });
        type = getTypeFromRoute(slidingToRoute as RouteLocationNormalizedGeneric);
        uid = getUidFromRoute(slidingToRoute as RouteLocationNormalizedGeneric);
      }
    }
    const doc = await cms.getByUid(type as CmsDocumentTypes, uid);
    return doc;
  }
  return null;
}

let currentDoc: CmsDocument | null = null;

const serverRenderedRoutesWithoutNavigation = ["/account", "/ErrorPages"];

router.afterEach((to, from) => {
  if (
    to.meta?.hideNavigation ||
    !!serverRenderedRoutesWithoutNavigation.find((path) => to.path.startsWith(path))
  ) {
    // Remove Navbar
    document.body.classList.remove("with-navbar");
    document.body.classList.add("no-navbar");
  } else {
    // Add Navbar
    document.body.classList.remove("no-navbar");
    document.body.classList.add("with-navbar");
  }

  if (
    csContext.userDocument?.platformPermissions.includes("FullMembershipAccess") &&
    !csContext.userDocument?.platformPermissions.includes("AdministerPlatform") &&
    !csContext.userDocument?.platformPermissions.includes("AssessPlanAccess") &&
    csContext.userDocument.hasCompletedFoundationsSession
  ) {
    document.body.classList.add("full-access");
  } else {
    document.body.classList.remove("full-access");
  }

  getDoc(to).then((doc) => {
    if (router?.currentRoute.value.fullPath === to?.fullPath) {
      if (doc && "title" in doc.data) {
        setTitle(cms.asText(doc.data.title));
      } else if (to.meta?.title) {
        setTitle(to.meta?.title as string);
      }
      currentDoc = doc;
    }

    if (to.meta?.trackingHook) {
      (to.meta as any).trackingHook(doc, (richText: CmsRichText) => {
        return cms.asText(richText);
      });
    }
  });
});

export function getCurrentDoc(): CmsDocument | null {
  return currentDoc;
}

export function goToPrismic(): void {
  let href: string;
  const id = getCurrentDoc()?.id;
  if (id) {
    href = `https://characterscope.prismic.io/documents~b=working&c=published&l=en-gb/${id}/`;
  } else {
    href = "https://characterscope.prismic.io";
  }

  window.open(href, "_blank");
}

export default router;
